import { useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  DbRoleWithActions,
  useGetApiUsersRolesQuery,
} from '../../../../app/athenaApiGen';
import EditRolePermissionsDialog from './EditRolePermissionsDialog';

// Read-only tabular view of the role catalog: one row per role with its
// classification metadata and a total-permissions count. Row actions
// launch an edit-permissions dialog that lets an admin add/remove role
// actions without falling back to the DB team.
const AdminRolesPanel = () => {
  const {
    data: rolesData = [],
    isFetching,
    error,
  } = useGetApiUsersRolesQuery();

  const [editingRole, setEditingRole] = useState<DbRoleWithActions | null>(
    null,
  );

  const rows = useMemo(
    () =>
      [...rolesData]
        .sort((a, b) => (a.roleName || '').localeCompare(b.roleName || ''))
        .map((role, idx) => ({
          id: role.roleName ?? `row-${idx}`,
          roleName: role.roleName ?? '',
          roleType: (role as any).roleType ?? '',
          source: (role as any).source ?? '',
          scope: (role as any).scope ?? '',
          permissionCount: (role.actions ?? []).length,
          // Stash the raw role so the row action can hand it to the dialog
          // without re-looking it up in rolesData.
          _rawRole: role,
        })),
    [rolesData],
  );

  const columns: GridColDef[] = [
    { field: 'roleName', headerName: 'Role Name', flex: 1.5, minWidth: 200 },
    { field: 'roleType', headerName: 'Role Type', flex: 1, minWidth: 140 },
    { field: 'source', headerName: 'Source', flex: 0.8, minWidth: 100 },
    { field: 'scope', headerName: 'Scope', flex: 0.8, minWidth: 100 },
    {
      field: 'permissionCount',
      headerName: 'Permissions',
      type: 'number',
      flex: 0.6,
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 60,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="edit"
          icon={
            <Tooltip title="Edit permissions">
              <EditIcon fontSize="small" />
            </Tooltip>
          }
          label="Edit"
          onClick={() =>
            setEditingRole((params.row as any)._rawRole as DbRoleWithActions)
          }
          showInMenu={false}
        />,
      ],
    },
  ];

  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        Failed to load roles.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Read-only catalog of every role in the system. Click the edit icon on a
        row to add or remove permissions for that role — no database script
        required. Drill into the "Role–Permission Mapping" item below to see the
        full permission bindings.
      </Typography>
      <Box sx={{ height: 480, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isFetching}
          density="compact"
          initialState={{
            sorting: {
              sortModel: [{ field: 'permissionCount', sort: 'desc' }],
            },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Box>

      <EditRolePermissionsDialog
        open={!!editingRole}
        role={editingRole}
        onClose={() => setEditingRole(null)}
      />
    </Box>
  );
};

export default AdminRolesPanel;
